import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Box, Mail, Maximize2, ScanBarcode, Pen, Archive, Weight } from "lucide-react";
import { toast } from "sonner";
import { usePackageStore } from "@/stores/packageStore";
import { PackageType } from "@/types";
import { ShippingLabelScanner } from "./ShippingLabelScanner";
import { usePackages } from '@/hooks/usePackages';
import { extractLabelFields } from '@/lib/api';
import { uploadFile } from '@/services/supabaseStorage';
import { runOcr } from '@/lib/api';

interface NewPackageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (pkg: any) => void;
}

type EntryMode = "scan" | "manual" | "method-selection";

const NewPackageModal = ({ open, onOpenChange, onSubmit }: NewPackageModalProps) => {
  // Modal state for entry mode and form fields
  const [entryMode, setEntryMode] = useState<EntryMode>("method-selection");
  const [packageType, setPackageType] = useState<PackageType>("Regular");
  const [tracking, setTracking] = useState("");
  const [guestName, setGuestName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [observations, setObservations] = useState({ oversized: false, overweight: false });
  
  const { addPackage } = usePackageStore();
  const { createPackage } = usePackages();

  // Handles form submission for registering a new package
  const handleSubmit = () => {
    if (!tracking || !guestName) {
      toast.error("Please fill all required fields");
      return;
    }
    // Remove all spaces from tracking number before slicing last 4 digits
    const cleanedTracking = tracking.replace(/\s+/g, "");
    const last4Digits = cleanedTracking.slice(-4);
    const newPkg: any = {
      tracking: last4Digits,
      guest_name: guestName,
      room_number: roomNumber,
      entry_date: new Date().toISOString(),
      observations,
      is_deleted: false,
      deletion_requested_at: null
    };
    if (onSubmit) {
      onSubmit(newPkg);
    } else {
      addPackage(newPkg);
      toast.success("Package registered successfully");
    }
    resetForm();
    onOpenChange(false);
  };
  
  // Resets the modal form to its initial state
  const resetForm = () => {
    setEntryMode("method-selection");
    setPackageType("Regular");
    setTracking("");
    setGuestName("");
    setRoomNumber("");
    setObservations({ oversized: false, overweight: false });
  };
  
  // Handles the result from the label scanner or debug upload
  const handleScanResult = async (result: { 
    imageBlob: Blob, 
    ocrText?: string 
  }) => {
    if (result.ocrText) {
      try {
        const extracted = await extractLabelFields(result.ocrText);
        const guestName = extracted.guest_name?.trim() || '';
        const trackingNumber = extracted.tracking_number?.replace(/\s+/g, '').slice(-4) || '';
        if (!guestName && !trackingNumber) {
          toast.error("Couldn't extract any information. Please take another picture.");
          return;
        }
        // Immediately create the package
        const pkg = {
          guest_name: guestName,
          tracking: trackingNumber,
          room_number: null,
          observations: { oversized: false, overweight: false },
          status: 'Pending',
        };
        try {
          const created = await createPackage(pkg, { silent: true });
          // Build submessage
          let subMsg = '';
          if (guestName) subMsg += `Guest Name: ${guestName}`;
          if (trackingNumber) subMsg += `${guestName ? '\n' : ''}Tracking #: ${trackingNumber}`;
          toast(
            <div>
              <div className="font-semibold">New Package Created</div>
              <div>A new package was created successfully using the shipping label scan.</div>
              {subMsg && <div className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{subMsg}</div>}
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={() => setEntryMode('scan')}>Scan Another</Button>
                <Button size="sm" variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>Close</Button>
              </div>
            </div>
          );
          resetForm();
          setEntryMode('scan');
        } catch (err) {
          toast.error("Failed to create package from scan. Please try again.");
        }
      } catch (err) {
        toast.error("OpenAI extraction failed. Try again.");
      }
    } else {
      toast.error("Could not extract text from image. Try retaking the photo.");
    }
  };
  
  // DEBUG: Manual image upload for testing OCR pipeline
  // This is only for local testing, not shown in production
  const handleDebugFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Immediately process the image: upload, OCR, extract fields
    let ocrText = '';
    try {
      const fileObj = new File([file], 'label.jpg', { type: file.type });
      const { url: imageUrl, error: uploadError } = await uploadFile({
          bucket: 'labels',
          path: `debug_scan_${Date.now()}.jpg`,
          file: fileObj,
      });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError}`);
      }

      const ocrResult = await runOcr(imageUrl);
      ocrText = ocrResult.text;
    } catch (err) {
      toast.error('Failed to process image: ' + (err instanceof Error ? err.message : err));
    }
    await handleScanResult({ imageBlob: file, ocrText });
  };
  
  const handleObservationToggle = (key: 'oversized' | 'overweight') => {
    setObservations(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const renderScanMode = () => (
    <div className="py-6">
      <ShippingLabelScanner
        onCapture={handleScanResult}
        onCancel={() => setEntryMode("method-selection")}
      />
      
      <div className="mt-4 text-center text-sm text-muted-foreground">
        <p>Position the shipping label within the frame and make sure it's clearly visible</p>
      </div>
      
      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={() => setEntryMode("method-selection")}>
          Back
        </Button>
        <Button onClick={() => setEntryMode("manual")}>
          Manual Entry
        </Button>
      </div>
    </div>
  );
  
  const renderMethodSelection = () => (
    <div className="py-6">
      <h3 className="text-lg font-medium text-center mb-6">Register a new package</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className="border rounded-lg p-6 flex flex-col items-center cursor-pointer hover:border-primary transition-colors"
          onClick={() => setEntryMode("scan")}
        >
          <div className="h-16 w-16 bg-secondary rounded-full flex items-center justify-center mb-4">
            <ScanBarcode className="h-8 w-8 text-primary" />
          </div>
          <h4 className="text-lg font-medium text-primary mb-2">Scan shipping label</h4>
          <p className="text-sm text-center text-muted-foreground">
            Use your camera to scan and extract information from the shipping label
          </p>
        </div>
        
        <div 
          className="border rounded-lg p-6 flex flex-col items-center cursor-pointer hover:border-primary transition-colors"
          onClick={() => setEntryMode("manual")}
        >
          <div className="h-16 w-16 bg-secondary rounded-full flex items-center justify-center mb-4">
            <Pen className="h-8 w-8 text-primary" />
          </div>
          <h4 className="text-lg font-medium text-primary mb-2">Manual entry</h4>
          <p className="text-sm text-center text-muted-foreground">
            Enter package information in the registration form
          </p>
        </div>
      </div>
      {/* DEBUG: Manual image upload for OCR pipeline */}
      <div className="mt-6 text-center">
        <label className="block text-xs text-red-500 font-bold mb-2">[DEBUG] Upload image from PC for OCR</label>
        <input type="file" accept="image/*" onChange={handleDebugFileUpload} />
      </div>
    </div>
  );
  
  const renderManualEntry = () => (
    <div className="py-4">
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2">Observations</h3>
          <div className="space-y-2">
            <div className="flex items-center">
              <button
                type="button"
                className={`p-2 rounded-full border ${observations.oversized ? "bg-purple-100 border-purple-400" : "bg-white border-gray-300"}`}
                onClick={() => handleObservationToggle("oversized")}
                aria-label="Toggle Oversized"
              >
                <Maximize2 size={20} className={observations.oversized ? "text-green-600" : "text-gray-400"} />
              </button>
              <span className="ml-3 text-sm">Oversized</span>
            </div>
            <div className="flex items-center">
              <button
                type="button"
                className={`p-2 rounded-full border ${observations.overweight ? "bg-purple-100 border-purple-400" : "bg-white border-gray-300"}`}
                onClick={() => handleObservationToggle("overweight")}
                aria-label="Toggle Overweight"
              >
                <Weight size={20} className={observations.overweight ? "text-green-600" : "text-gray-400"} />
              </button>
              <span className="ml-3 text-sm">Overweight</span>
            </div>
          </div>
        </div>
        
        <div>
          <label htmlFor="tracking" className="block text-sm font-medium mb-1">
            Tracking Number
          </label>
          <Input
            id="tracking"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="Enter full tracking number"
            className="w-full"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Only the last 4 digits will be saved
          </p>
        </div>
        
        <div>
          <label htmlFor="guestName" className="block text-sm font-medium mb-1">
            Guest Name
          </label>
          <Input
            id="guestName"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Guest full name"
            className="w-full"
          />
        </div>
        
        <div>
          <label htmlFor="roomNumber" className="block text-sm font-medium mb-1">
            Unit Number (optional)
          </label>
          <Input
            id="roomNumber"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            placeholder="e.g. 2304"
            className="w-full"
          />
        </div>
      </div>
      
      <div className="mt-6 flex justify-between">
        <Button 
          variant="outline" 
          onClick={() => setEntryMode(entryMode === "scan" ? "scan" : "method-selection")}
        >
          Back
        </Button>
        <Button onClick={handleSubmit}>
          Register Package
        </Button>
      </div>
    </div>
  );
  
  const renderContent = () => {
    switch (entryMode) {
      case "method-selection":
        return renderMethodSelection();
      case "scan":
        return renderScanMode();
      case "manual":
        return renderManualEntry();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) resetForm();
      onOpenChange(value);
    }}>
      <DialogContent className="max-w-md" aria-describedby="new-package-modal-desc">
        <span id="new-package-modal-desc" style={{ display: 'none' }}>
          Fill out the form to register a new package. Required fields are marked with *.
        </span>
        <DialogHeader>
          <DialogTitle>New Package</DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default NewPackageModal;
